import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type ServiceUsage, useRequestServiceBudget } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import { formatAmount, parseBudget, SERVICE_LABEL } from "@/lib/services-gateway";

import { BudgetInput } from "./budget-input";

/** Ask an admin for a bigger monthly budget for one service. */
export function RequestBudgetForm({
  projectId,
  usage,
  onClose,
}: {
  projectId: number;
  usage: ServiceUsage;
  onClose: () => void;
}) {
  const ask = useRequestServiceBudget(projectId);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const budget = parseBudget(usage.unit, amount);
  const canSubmit = budget !== null && reason.trim().length >= 10 && !ask.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || budget === null) {
      return;
    }
    setError(null);
    try {
      await ask.mutateAsync({ service: usage.service, budget, reason: reason.trim() });
      toast.success("Request sent. An admin decides it in ScaffoldUI.");
      onClose();
    } catch (err) {
      logger.error("[services] budget request failed:", err);
      setError(readApiErrorMessage(err, "Could not send the request. Please try again."));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Request a bigger budget</DialogTitle>
        <DialogDescription>
          {SERVICE_LABEL[usage.service]}: an admin decides, and may grant a different amount (at
          most {formatAmount(usage.unit, usage.max_monthly)} a month). Until then the current budget
          applies.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="space-y-4">
          <BudgetInput unit={usage.unit} value={amount} onChange={setAmount} id="ask-budget" />
          <div className="space-y-1.5">
            <Label htmlFor="ask-reason">Why is it needed?</Label>
            <Textarea
              id="ask-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="What the extra use is for (at least 10 characters)"
            />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={ask.isPending}>
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {ask.isPending ? "Sending…" : "Send request"}
        </Button>
      </DialogFooter>
    </>
  );
}
